import * as Sentry from '@sentry/vue'
import type { AxiosError } from 'axios'
import type { App } from 'vue'
import type { Router } from 'vue-router'

import { getCommitSha } from '@gto/helpers/build-info'
import { IS_PROD_ENV } from '@gto/helpers/env-checkers'

const SENTRY_LOG_PREFIX = '@GW: '
export function logSentry(...messages: unknown[]) {
  console.debug(SENTRY_LOG_PREFIX, ...messages)
}

export function isSentryLogMessage(message: string | undefined): boolean {
  return message?.startsWith(SENTRY_LOG_PREFIX) ?? false
}

/**
 * Read an HTTP error's response by shape. `in` narrowing rather than
 * `isAxiosError` on purpose: axios is a TYPE-only import here, and a shared
 * lib must not take a runtime dependency on one HTTP client.
 */
const readHttpResponse = (error: unknown): { status: number; data: unknown } | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined
  }
  const { response } = error
  if (typeof response !== 'object' || response === null || !('status' in response)) {
    return undefined
  }
  const { status } = response

  return typeof status === 'number'
    ? { status, data: 'data' in response ? response.data : undefined }
    : undefined
}

/**
 * Report an HTTP failure that `captureAxiosError` drops — it returns early on
 * EVERY 4xx, so a rejected request otherwise leaves no trace at all.
 *
 * Never hands Sentry the Error itself: `captureConsoleIntegration` routes an
 * Error argument to `captureException`, which leaves `event.message` undefined,
 * and `beforeSend` then drops the debug event for failing the `@GW:` prefix
 * test. Passing plain values keeps the report searchable by `label` and status.
 */
export const logSentryHttpError = (label: string, error: unknown) => {
  const response = readHttpResponse(error)
  if (response === undefined) {
    logSentry(`${label}: `, error instanceof Error ? error.message : error)

    return
  }
  logSentry(`${label}: ${response.status} `, response.data)
}

export const captureAxiosError = (
  error: AxiosError,
  clientType: 'vue' | 'react' | 'unknown' = 'unknown',
  /** Optional call-site label so multiple queries sharing a URL stay distinguishable in Sentry. */
  label?: string,
) => {
  const status = error.response?.status
  if (status !== undefined && status >= 400 && status < 500) {
    return
  }
  Sentry.captureException(error, {
    contexts: {
      // Axios types `headers` as required, but this helper runs in the error path
      // and may receive mock or interceptor-synthesised errors without it. A throw
      // here would silently break Sentry capture, so the optional chain stays.
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      'backend-trace': { id: error.response?.headers?.traceresponse ?? 'unknown' },
    },
    tags: {
      'client-type': clientType,
      ...(label !== undefined && { label }),
      // Axios puts the status only in the exception message, so slicing an issue by status means
      // grouping on free text (FRONTEND-2KMX mixes 500s and 503s this way). A tag makes it an
      // indexed facet instead — issue search, tag breakdowns, alert conditions — on a key
      // `solver-service` and `pokerwizard-ios` already emit, so one query shape spans them.
      ...(status !== undefined && { 'http.status_code': String(status) }),
    },
  })
}

// INT-15: a partner import link carries a hand history in its fragment
// (`/analyze/import#hh=<base64url>&src=…`). The document's first inline script strips it
// before any tracker runs; this is the fallback if that script is ever bypassed. The `hh`
// value is replaced with a placeholder so the URL still shows that a hand was present.
// Every `hh` parameter in the fragment, not only the first: a crafted link can repeat it.
const HAND_FRAGMENT_VALUE_RE = /(^|&)hh=[^&]*/g
const HAND_PLACEHOLDER = '[hand]'

// Exported for the unit test: `beforeSend` only exists inside `initSentry`'s prod branch.
export const scrubHandFragment = (url: string): string => {
  const hashIndex = url.indexOf('#')

  if (hashIndex === -1) {
    return url
  }

  const fragment = url
    .slice(hashIndex + 1)
    .replace(HAND_FRAGMENT_VALUE_RE, `$1hh=${HAND_PLACEHOLDER}`)

  return `${url.slice(0, hashIndex + 1)}${fragment}`
}

type ScrubbableEvent = { request?: { url?: string } }

export const scrubHandFragmentFromEvent = <E extends ScrubbableEvent>(event: E): E => {
  const { request } = event

  if (request?.url !== undefined) {
    request.url = scrubHandFragment(request.url)
  }

  return event
}

type ScrubbableBreadcrumb = { data?: Record<string, unknown> }

// The `replaceState` that strips the fragment is itself recorded as a `navigation`
// breadcrumb whose `from` is the URL that still carried the hand.
export const scrubHandFragmentFromBreadcrumb = <B extends ScrubbableBreadcrumb>(
  breadcrumb: B,
): B => {
  const { data } = breadcrumb

  if (data === undefined) {
    return breadcrumb
  }

  for (const key of ['from', 'to']) {
    const value = data[key]

    if (typeof value === 'string') {
      data[key] = scrubHandFragment(value)
    }
  }

  return breadcrumb
}

// The frame fields every filter below reads. `beforeSend` runs PRE-symbolication, so
// `filename` is a hashed asset and `function` is a minified name — only fields that survive
// minification (or that the SDK synthesizes itself) are safe to key on.
type StackFrameLike = {
  filename?: string
  function?: string
  lineno?: number
  colno?: number
}

// Pulls the message, exception class, stack frames + mechanism out of a Sentry event the same
// way every beforeSend filter below consumes them. Exported so tests can exercise the
// real extraction path instead of re-implementing it.
export const getExceptionDetails = <F extends StackFrameLike>(event: {
  message?: string
  exception?: {
    values?: readonly {
      type?: string
      value?: string
      mechanism?: { type?: string }
      stacktrace?: { frames?: readonly F[] }
    }[]
  }
}): {
  msg: string
  exceptionType: string
  frames: readonly F[]
  mechanismType: string
} => {
  const exception = event.exception?.values?.[0]

  return {
    msg: exception?.value ?? event.message ?? '',
    // The exception CLASS (`DataCloneError`, `TypeError`, …). Sentry keeps it in its own field,
    // so it is NOT reliably present in `value` — for a DOMException that carries a stack (every
    // Chromium build) the class is stripped from the message. Keying a filter on a
    // `"DataCloneError: …"` message prefix would therefore be a silent no-op there.
    exceptionType: exception?.type ?? '',
    frames: exception?.stacktrace?.frames ?? [],
    mechanismType: exception?.mechanism?.type ?? '',
  }
}

// FRONTEND-2WST / FRONTEND-2H6A: Vue's production runtime patcher throws
// `Cannot read properties of null (reading 'insertBefore' | 'removeChild')` on the Practice
// Trainer, whose heavy `v-if` toggling (plus a Teleport and a React-via-module-federation
// overlay) intermittently desyncs Vue's prod vdom from the live DOM. The throw is
// unrecoverable at its site and self-amplifying: once desynced, every later patch re-throws,
// so one broken session emits hundreds of identical events (the FE-615 rename rebuild
// surfaced this latent bug at scale). The Practice route boundary remounts to recover the
// UI; this regex is exported so it can detect the same error from a raw `errorCaptured`
// payload (which has no Sentry mechanism). Retire once PracticeTrainer.vue's v-if usage is
// hardened (deferred follow-up).
// FE-714: broadened to the parentNode / nextSibling / emitsOptions forms of the same desync
// (FRONTEND-2H6B, 305E, 2H7E, 2XYK) — once the vdom is desynced the patcher reads whichever
// null vdom-node field (parentNode/nextSibling/insertBefore/removeChild) or component-instance
// field (emitsOptions, read during emit()) it reaches first, so all five are the same bug
// surfacing on different properties.
// https://gtowizard.sentry.io/issues/7047738400/ (FRONTEND-2WST)
// https://gtowizard.sentry.io/issues/6547998817/ (FRONTEND-2H6A)
export const VUE_VDOM_DESYNC_MESSAGE_RE =
  /Cannot read properties of null \(reading '(?:insertBefore|removeChild|parentNode|nextSibling|emitsOptions)'\)|null is not an object \(evaluating '[^']*\.(?:insertBefore|removeChild|parentNode|nextSibling|emitsOptions)'\)/

// The `auto.function.vue.error_handler` mechanism gate is a WEAK discriminator, not a clean
// first-party/third-party split: @sentry/vue routes essentially every component
// render/lifecycle/handler error through this mechanism, so a genuine first-party null-read
// on one of these DOM/vdom properties (parentNode/nextSibling especially) matches too. What
// bounds the false-drop risk is that this class is only SAMPLED (1-in-50, below), never
// dropped, and its survivors are tagged `sampled_noise=vue_vdom_desync` so a real crash
// stays greppable among the flood (FE-714, R-a). The gate still excludes non-Vue-handler
// crashes of the same shape.
const VUE_ERROR_HANDLER_MECHANISM = 'auto.function.vue.error_handler'

export const isVueVdomDesyncError = (message: string, mechanismType: string): boolean =>
  mechanismType === VUE_ERROR_HANDLER_MECHANISM && VUE_VDOM_DESYNC_MESSAGE_RE.test(message)

// Shared sample rate for every noise class below: surface 1 event, then drop the next 49,
// repeat — the trend stays visible without the flood drowning every other signal.
// KEEP IN SYNC with the inlined copies in apps/gto-wizard-web-vue/index.html's pre-SPA loader.
const NOISE_SAMPLE_EVERY = 50

// FE-744 / FRONTEND-2ZM8: the Vue trainer posts a non-cloneable payload over a BroadcastChannel
// (a shallow `toRaw` of nested reactive proxies), so `postMessage` throws a DataCloneError —
// 218 ev / 2 users / 7d, a single-day spike. High-volume + near-zero user reach = noise, but it
// is OUR bug, not a third party's, so this is SAMPLED + tagged rather than dropped (same reason
// as isVueVdomDesyncError: a 100% drop would make a real defect invisible). ~218 ev -> ~5 ev/7d.
//
// Gated on the Vue error-handler mechanism so the identical React failure — which would be a
// genuine, actionable bug in the new app — is never swept in. A message-only match would take
// both.
//
// The DataCloneError CLASS is read from the exception `type` field, never from a message prefix:
// a DOMException that carries a stack (every Chromium build) has its class stripped from
// `value`, so matching `/^DataCloneError/` on the message would be a silent no-op there. The
// message test then only has to confirm the clone-failure shape, whose phrasing differs across
// engines ("could not be cloned" / "can not be cloned").
//
// The clone-message clause is intentionally the ticket's broad form: it also matches a Vue
// DataCloneError from Worker.postMessage / structuredClone / IndexedDB, not only BroadcastChannel.
// Kept broad on purpose — narrowing to require "BroadcastChannel" in the message would silently
// miss the WebKit phrasing of the very trainer bug we target (its message may omit the channel
// name). The class is only SAMPLED + tagged, so a genuinely new Vue clone bug still surfaces as a
// trend rather than vanishing.
// NOT mirrored into apps/gto-wizard-web-vue/index.html's pre-SPA loader: that Sentry.init passes no
// `app`, so @sentry/vue's error handler — the only source of the `auto.function.vue.error_handler`
// mechanism this filter gates on — is never wired there, and the class cannot fire before the app
// mounts. (Same reason isVueVdomDesyncError, the other Vue-handler-gated filter, is SPA-only.)
const CLONE_FAILURE_MESSAGE_RE = /(?:could not|can ?not|couldn't) be cloned/i

export const isVueBroadcastChannelCloneError = (
  message: string,
  exceptionType: string,
  mechanismType: string,
): boolean =>
  mechanismType === VUE_ERROR_HANDLER_MECHANISM &&
  exceptionType === 'DataCloneError' &&
  (CLONE_FAILURE_MESSAGE_RE.test(message) || message.includes('BroadcastChannel'))

// Stealth-automation bots (puppeteer-extra-plugin-stealth and clones) inject a
// script that wraps Object.getOwnPropertyDescriptor and recurses infinitely.
// The resulting stack overflow has every frame in <anonymous> — it never touches
// our bundles, so a real recursion bug in app code is never matched by this check.
// Deliberately matches only the V8/JSC message: the bots are Chromium-based, so
// Firefox's "too much recursion" is left unfiltered on purpose.
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA loader
// (it runs before any ES module and cannot import this lib).
// https://gtowizard.sentry.io/issues/6980536740/ (FRONTEND-2W3F)
export const isInjectedScriptStackOverflow = (
  message: string,
  frames: readonly { filename?: string }[],
): boolean => {
  if (!message.includes('Maximum call stack size exceeded')) {
    return false
  }
  if (frames.length === 0) {
    return false
  }

  return frames.every((frame) => frame.filename === undefined || frame.filename === '<anonymous>')
}

// FE-485: third-party scripts and browser extensions throw / reject NON-Error
// values — a plain {code,message} object from an EIP-1193 wallet extension
// (FRONTEND-DE5, ~16k), or a cross-origin "Script error." (FRONTEND-2PNZ, ~31k).
// (The former #1 member — the FRONTEND-DSX resource-load `Event` (type=error), ~130k
// events — is now dropped 100% upstream by isResourceLoadErrorEvent before this sampler
// runs, FE-714; this regex still recognizes its shape but no longer sees it.)
// The browser's global onerror / onunhandledrejection handlers capture these, but
// the value carries NO JavaScript stack, so Sentry can only synthesize a message
// and the event shows as <unknown> / "No error message" forever — sourcemaps cannot
// symbolicate what has no frames. Sentry synthesizes "<X> captured as
// (promise rejection|exception)" ONLY for non-Error values (a real Error keeps its
// own message + stack), so that phrase combined with an empty frame list is a
// precise, safe drop signal. The frame guard means a genuine app-code throw — which
// always carries app frames — is never dropped. This replaces, for the CURRENT SDK
// message format, the legacy `ignoreErrors` entry "Non-Error promise rejection
// captured with keys: ..." which only matched the older "...captured with keys"
// wording and silently stopped catching these once the SDK changed its synthesized
// message; that entry is kept as belt-and-suspenders for any client still on the
// old format.
//
// The pattern is ANCHORED to the start of the message so it can only match the
// SDK's own synthesized boilerplate, never the same phrase buried inside a genuine
// first-party message. The optional prefix is restricted to the JS value-class /
// DOM constructor names the SDK actually emits (not any leading word) so a frameless
// first-party message that merely opens with "<word> captured as …" is not swept in.
// It covers every observed/documented synthesis form:
//   Event `Event` (type=error) captured as promise rejection  (FRONTEND-DSX — now dropped upstream, FE-714)
//   Event `ErrorEvent` captured as exception with message `Script error.`
//   Object captured as promise rejection with keys: code, message
//   captured as promise rejection with keys: …   (Object class, some SDK versions)
//
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA
// loader (it runs before any ES module and cannot import this lib).
const NON_ERROR_CAPTURE_RE =
  /^(?:Event `[^`]+`.*? |(?:Object|Array|String|Number|Boolean|Symbol|BigInt|Function|Promise|Error[A-Za-z]*|[A-Za-z]*Event) |)captured as (?:promise rejection|exception)\b/
const BARE_SCRIPT_ERROR_RE = /^Script error\.?$/

// FE-725: a bare non-Error global throw (`throw undefined` on the SolutionsV2React MF
// remote) surfaces as `Uncaught <primitive>` — `Uncaught undefined` (FRONTEND-30BA /
// FRONTEND-2Y46), also `null` / `[object Object]` / a bare number / Symbol. A real Error
// keeps its class + message ("Uncaught TypeError: …"), so this deliberately does NOT match
// those forms — they stay actionable. Anchored to the WHOLE message so `Uncaught undefined
// is not a function` (a genuine ReferenceError phrasing) is never swept in.
const NON_ERROR_UNCAUGHT_RE =
  /^Uncaught (?:undefined|null|true|false|NaN|-?Infinity|-?\d+(?:\.\d+)?|\[object [^\]]*\]|Symbol\(.*\))$/

// FE-744: the frame Sentry's global `onerror` handler SYNTHESIZES when the browser handed it no
// location at all — the cross-origin "Script error." case, where the spec forces
// `url=""`, `lineno=0`, `colno=0` precisely so the page learns nothing about the foreign script.
// `_enhanceEventWithInitialFrame` (@sentry/browser globalhandlers.js) then pushes ONE frame:
//   { colno: 0, filename: getFilenameFromUrl(url) ?? getLocationHref(), function: '?' /* UNKNOWN_FUNCTION */,
//     in_app: true, lineno: 0 }
// so `filename` falls back to the PAGE HREF and `in_app` is true. That is why the old
// `frames.length === 0` guard never fired on FRONTEND-2PNZ (frames.length === 1, in-app-only)
// and why matching `filename === '<anonymous>'` here would be a SILENT NO-OP — the filename is
// `https://app.gtowizard.com/<route>`, never `<anonymous>`. The load-bearing property is that
// the frame pins NO THROW SITE: no function name, no line, no column.
//
// This is exactly what keeps a genuine throw actionable. A SAME-ORIGIN non-Error throw
// (`throw { code: 1 }` in our own code) reaches the very same handler, but the browser hands it
// a real `url` + `lineno` + `colno`, so its initial frame carries the real script and line — it
// does NOT match, and is never suppressed.
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA loader.
const isLocationlessSyntheticFrame = (frame: StackFrameLike): boolean => {
  // Any real position pins a throw site, so the frame is actionable no matter its filename —
  // never location-less. This gate is what keeps a frame that names a function + line/col but
  // happens to carry no filename OUT of the suppression: absence of a filename alone must not
  // classify a positioned frame as synthetic.
  const pinsNoPosition = (frame.lineno ?? 0) === 0 && (frame.colno ?? 0) === 0
  if (!pinsNoPosition) {
    return false
  }

  // Two DISTINCT position-less synthetic sources — not one duplicated case:
  //
  //  1. Wallet-extension / injected-script capture: the frame maps to NO source file at all
  //     (`filename` undefined, or the literal `<anonymous>` those scripts emit). With no file
  //     AND no throw site it is unsymbolicatable — but a source-less frame that still names a
  //     real function is a throw site we can grep for, so it survives (`function` must be
  //     undefined or the `?` placeholder). This `<anonymous>` case is the extension shape; it is
  //     NOT the SDK synthetic frame in (2), which always carries the page href.
  const namesNoSourceFile = frame.filename === undefined || frame.filename === '<anonymous>'
  if (namesNoSourceFile) {
    return frame.function === undefined || frame.function === '?'
  }

  //  2. The SDK-synthesized initial frame for the cross-origin "Script error." case: its filename
  //     is the useless PAGE href (never `<anonymous>`), and its function is the literal
  //     UNKNOWN_FUNCTION `?` placeholder. `function === '?'` must stay STRICT here — a real app
  //     frame that names the chunk via its filename but merely lacks a function is still
  //     actionable, so a MISSING function must not match.
  return frame.function === '?'
}

export const isUnsymbolicatableNonError = (
  message: string,
  frames: readonly StackFrameLike[],
): boolean => {
  // FE-725: a bare non-Error primitive throw (`Uncaught undefined`) carries NO stack of its
  // own — a thrown primitive captures no call site, so whatever frames Sentry attaches are
  // synthesized by the global handler / React 19's `reportGlobalError` re-dispatch, never the
  // throw origin. FRONTEND-30BA (window.onerror, frameless) and FRONTEND-2Y46 (the SAME throw
  // re-dispatched by React with a single minified vendor react-dom frame) are one phenomenon.
  // Verified against live 2Y46 events: pre-symbolication the frame is
  // `{ filename: 'assets/[hash].js', function: '$s' }` — the readable
  // `react-dom-client.production.js` / `reportGlobalError` names exist only in the server-side
  // sourcemap, which `beforeSend` never sees — so the class is unsymbolicatable regardless of
  // frame count. Still 1-in-50 sampled + tagged below (never a 100% drop), so a genuine future
  // regression (e.g. a real app `throw undefined`) stays visible as a trend.
  if (NON_ERROR_UNCAUGHT_RE.test(message)) {
    return true
  }

  // The SDK-synthesized non-Error captures (wallet-extension objects, cross-origin
  // "Script error.") only drop when there is nothing to symbolicate — any frame that pins a real
  // throw site means we CAN act, and the event survives.
  //
  // FE-744: "nothing to symbolicate" is NOT the same as "no frames". The SDK always attaches its
  // one synthesized initial frame to an `onerror` capture, so the old `frames.length === 0` test
  // let the entire cross-origin "Script error." class (FRONTEND-2PNZ, 825 ev / 542 users / 7d)
  // bypass the sampler below — 823/823 events reached Sentry untagged. A frame that carries no
  // function, line or column is not evidence of an actionable stack; treat it as frameless.
  const isNonErrorCapture =
    NON_ERROR_CAPTURE_RE.test(message) || BARE_SCRIPT_ERROR_RE.test(message.trim())

  return isNonErrorCapture && (frames.length === 0 || frames.every(isLocationlessSyntheticFrame))
}

// FE-714: the FRONTEND-DSX resource-load `Event` subclass was the #1 raw-event line
// (~130k events) and is definitionally unactionable — a frameless DOM `Event` (type=error)
// from a <script>/<link>/<img> onerror, no JS stack to symbolicate. It is a STRICT SUBSET of
// NON_ERROR_CAPTURE_RE: the ``Event `Event` (type=error)`` signature uniquely separates it
// from the wallet (`Object … with keys: code, message`) and cross-origin
// (`ErrorEvent … Script error.`) siblings. beforeSend drops this 100% *before* the umbrella
// non-Error sampler, so the siblings keep their 1-in-50 SDK-drift canary (the sampler is
// untouched). The `frames.length === 0` guard is preserved: any event carrying an app frame
// is never dropped. Caveat (R-g): frameless-ness does NOT distinguish first-party from
// third-party resource loads — a first-party chunk/asset 404 in this shape would go from
// ~2% sampled to 100% dropped. First-party chunk failures typically surface instead as the
// already-filtered `Failed to fetch dynamically imported module` string; verify via live
// Sentry before relying on the 100% drop (Open Question Q1) and fall back to a thin residual
// sample if any first-party asset-failure shape appears.
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA loader.
const RESOURCE_LOAD_EVENT_RE =
  /^Event `Event` \(type=error\) captured as (?:promise rejection|exception)\b/

export const isResourceLoadErrorEvent = (message: string, frames: readonly unknown[]): boolean =>
  frames.length === 0 && RESOURCE_LOAD_EVENT_RE.test(message)

// FE-485: this noise class is unactionable, but a 100% silent drop would make us
// blind to the filter itself breaking — exactly how the legacy `ignoreErrors` entry
// failed when the SDK message format drifted. Keep a sample, tagged
// `sampled_noise=non_error_global_capture`, so the class stays visible as a low-volume trend
// without the flood drowning real signal.
//
// FE-744: the 1-in-50 COUNTER alone cannot deliver that. It lives in a `beforeSend` closure, so
// it resets on every page load, and `count % 50 === 1` means the FIRST matching event of every
// session is always ingested. That throttles a flood WITHIN one session (the Vue vdom desync:
// hundreds of events in a single session) but does nothing for a class spread thinly across many
// sessions — FRONTEND-2PNZ averages ~1.5 events per session across 542 users, so a per-session
// counter would still ingest ~one event per user (~550/7d: a 1.5x cut, not the ~50x this class
// needs). Fixing the frame guard above without this would tag the class correctly and barely
// move its volume.
//
// So gate the class on a per-SESSION coin flip as well, drawn once at init: ~2% of sessions
// report it (and inside those, the counter still caps a flooding session at 1-in-50); the other
// ~98% drop it outright. Expected ~17 ev/7d, while ~11 canary sessions a week keep the
// SDK-drift canary alive. Deliberately NOT applied to the vdom-desync or Vite-preload samplers:
// those are within-session floods affecting few users, where a 2% session gate would take the
// canary to zero.
// FE-753: the same rate now also gates the signal-timeout and fetch-abort samplers below —
// the same cross-session shape; the class-specific numbers above describe the original
// non-Error class.
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA loader.
const NOISE_CANARY_SESSION_RATE = 1 / NOISE_SAMPLE_EVERY

// Exported so the sampler arithmetic is testable without booting `Sentry.init` — the counters
// themselves live in a closure and are otherwise unreachable from a unit test.
// FE-753: also reused verbatim by the signal-timeout and fetch-abort samplers below — same
// arithmetic, independent counters and session flips per class.
export const shouldIngestSampledNoise = (
  isCanarySession: boolean,
  eventCount: number,
  sampleEvery: number = NOISE_SAMPLE_EVERY,
): boolean => isCanarySession && eventCount % sampleEvery === 1

// FE-725 / FRONTEND-30E0: iOS Safari aborts in-flight fetches on navigation / backgrounding,
// so Vite's modulepreload polyfill (`__vitePreload`) rejects with a bare `TypeError: Load
// failed` (WebKit) / `Failed to fetch` (Chromium) surfaced via `onunhandledrejection`. This
// is almost always a cancelled preload, not a real load failure, and leaks past the FE-482
// hidden-tab guard because it fires while the tab is still `visible` (the abort races the
// visibility change).
//
// Keyed off the unhandled-rejection MECHANISM, not the stack frames. `beforeSend` runs
// pre-symbolication, so the frame it sees is minified — verified against live FRONTEND-30E0
// (456 events / 50 users, culprit `StudySolutions`): the polyfill frame is
// `{ filename: '/assets/[hash].js', function: 'l' }`, never the readable `__vitePreload`
// (that name is sourcemap-only, resolved server-side). A `__vitePreload` frame-function guard
// was therefore a silent no-op — the same failure mode that killed this PR's react-dom frame
// matcher. The mechanism is SDK metadata, so it survives minification: the abort always
// arrives as an unhandled rejection (live `mechanism` tag: 452× the namespaced
// `auto.browser.global_handlers.onunhandledrejection` + 7× the bare `onunhandledrejection`,
// both suffix-matched below). Scope stays tight: only a whole-message `Load failed` /
// `Failed to fetch` that arrived unhandled matches — a handled first-party fetch
// (`captureException`, mechanism `generic`) or a richer message (`Failed to fetch <url>`) is
// never swept in.
//
// FE-753 / FRONTEND-30E0 + 308C: WebKit may append the fetch TARGET as ` (app.gtowizard.com)`,
// so the `$`-anchored bare form matched the untagged sibling but rejected these two — they
// leaked UNSAMPLED at full volume while the bare form was correctly tagged. The optional host
// group closes that gap. It stays `$`-anchored and confined to a `gtowizard.com` subdomain, so
// `Failed to fetch dynamically imported module <url>` (the real chunk failure we must see) and
// a look-alike suffix (`evil.gtowizard.com.attacker.io`) both still fall through — verified in
// sentry.test.ts. NOT verified against a raw pre-`beforeSend` event value: Sentry access was
// unavailable at implementation time, and the suffix was inferred from `metadata.value` plus
// the tag asymmetry between the two issues. If the live value carries no suffix this widening
// is simply a no-op and the class keeps reporting exactly as it does today. Post-deploy
// validation checks which way it landed (same fail-safe posture as the FRONTEND-30RH frame gate
// below).
//
// This constant feeds only gates that SAMPLE or EXCLUDE — never a 100% drop. The annotated form
// must never reach the tracker FRAME gate: see BARE_FETCH_FAILURE_RE below for why.
//
// Deliberately CASE-SENSITIVE (no `/i`), matching BARE_FETCH_FAILURE_RE. Both engines emit these
// as fixed-case literals and URL hosts arrive lowercase-normalized, so `/i` would buy nothing —
// but it would newly match `load failed` / `failed to fetch`, which report in full today, and
// start sampling them. That is over-suppression, the one direction this file must never move in.
//
// SAMPLED, not 100%-dropped (guardrail): unlike a cancelled navigation, WebKit reuses the
// bare `Load failed` message for EVERY fetch failure — offline, DNS, CDN outage, chunk-404
// at the network layer. So a real iOS preload-network regression looks identical to the
// abort here. Keep the 1-in-50 canary (tagged `sampled_noise=vite_preload_fetch_abort`) so
// such a regression stays visible as a trend instead of vanishing. (Genuine dynamic-import
// failures also still surface via the vite:preloadError retry path in retry-import.ts and
// the `Failed to fetch dynamically imported module` ignoreErrors entry.)
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA loader.
const VITE_PRELOAD_MESSAGE_RE =
  /^(?:TypeError: )?(?:Load failed|Failed to fetch)(?: \([a-z0-9.-]+\.gtowizard\.com\))?$/

// The BARE, unannotated fetch-failure forms — the pre-FE-753 shape, kept as its own constant
// because the tracker FRAME gate must key on exactly this and never on the annotated form.
//
// The frame gate proves only "a tracker is somewhere on the stack", NOT "the tracker is what we
// failed to fetch" — and trackers legitimately run FROM our own stack (that is why it uses
// `frames.some`). So the moment it admits ` (<host>.gtowizard.com)`, a first-party chunk failure
// (`Load failed (app.gtowizard.com)`) that merely happens to carry a gtag frame is 100%-dropped —
// silencing precisely the module-federation / chunk regression FE-735's R2 work exists to
// surface, and the PostHog proxy (`zyx`) the host list is curated to protect. Keep the two apart.
//
// Splitting them costs no coverage. Both regexes are case-sensitive, so VITE_PRELOAD_MESSAGE_RE
// differs from this one by exactly the optional host group — and that group admits only a
// `gtowizard.com` suffix, whose sole tracker (`xyz`) is already an unconditional drop via
// THIRD_PARTY_TRACKER_TARGET_RE. Every remaining message that group would add to the frame gate
// therefore names a FIRST-PARTY target: pure false positives, zero true positives. (That
// equivalence is what a `/i` on either regex would silently break — it would admit case-variants
// that name no target at all, and no engine emits those, so they could only be first-party
// errors whose message happens to read that way. This gate would then 100%-drop them on an
// incidental tracker frame. `/i` is safe on THIRD_PARTY_TRACKER_TARGET_RE for the opposite
// reason — see there: a match anchored on a named target cannot land on a first-party error.)
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA loader.
const BARE_FETCH_FAILURE_RE = /^(?:TypeError: )?(?:Load failed|Failed to fetch)$/

// The SDK tags the global unhandled-rejection handler as
// `auto.browser.global_handlers.onunhandledrejection` (v8+) or the bare `onunhandledrejection`
// (older) — match the suffix so both forms hold.
const UNHANDLED_REJECTION_MECHANISM_SUFFIX = 'onunhandledrejection'

export const isVitePreloadFetchAbort = (message: string, mechanismType: string): boolean =>
  VITE_PRELOAD_MESSAGE_RE.test(message.trim()) &&
  mechanismType.endsWith(UNHANDLED_REJECTION_MECHANISM_SUFFIX)

// FE-744 / FRONTEND-308C + 30E0 + 30RF: third-party ANALYTICS TRACKERS reject their own
// `fetch()` calls (ad-blockers, consent walls, offline) and the rejection surfaces as the same
// `Failed to fetch` / `Load failed` as a real one — 257 ev / ~50 users / 7d. A tracker's
// failed beacon can never become our bug, so this is a 100% drop (unlike the sampled classes
// above, which could all regress into something real).
//
// Always keyed on a TRACKER HOST, never on the fetch-failure message alone: a blanket
// `/Failed to fetch|Load failed/` drop would also swallow module-federation remote-chunk load
// failures and user-facing `fetch()` errors, which are exactly the errors we must keep. Both
// gates also require the unhandled-rejection mechanism, so a handled first-party
// `captureException` (mechanism `generic`) never matches.
//
// There are two gates because the tracker host reaches us two different ways:
//   (a) as a stack FRAME     — the tracker's own wrapper is on the stack (308C / 30E0);
//   (b) as the fetch TARGET  — WebKit names the host in the message and the only frame is our
//                              own `/hoph/…` wrapper, so (a) never fires (30RF).
//
// The two gates read DIFFERENT message forms, and that split is load-bearing rather than
// incidental. When a message names its target, that annotation is strictly better evidence of
// whose fetch failed than "some tracker frame is on the stack" — so an annotated message is
// gate (b)'s business alone, and gate (a) stays on the bare form (BARE_FETCH_FAILURE_RE). If
// the two ever disagree, the target annotation wins.
//
// Matches any frame rather than only the innermost one, mirroring the `fromFreshchat` drop
// below: `denyUrls` tests just the innermost frame and would miss an obfuscated tracker wrapper.
// (Trackers are legitimately invoked FROM our own code — a gtag call is on our stack — so an app
// frame co-existing with the tracker frame is expected and does not exempt the beacon; `some` is
// deliberate.) Anchored at the scheme (`^https?://host/`) so the host must be the URL authority:
// a loose `/gtowizard\.com/` would match `app.gtowizard.com`, and an unanchored `//host/` would
// also match the host embedded in a first-party proxy's query string — both would swallow real
// first-party errors.
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA loader.
//
// The host list is the single source of truth for both gates below — a tracker added to one
// must never be missing from the other. `xyz.gtowizard.com` is the CNAME-cloaked GTM endpoint;
// it is NOT `zyx.gtowizard.com`, the PostHog ingest proxy, which is first-party infrastructure
// and deliberately absent from this list.
//
// The non-`gtowizard.com` `xyz.` hosts are PRE-ADDED: no app that reports through THIS lib loads
// one yet. Revisit an entry when one does. "Adopts Sentry" is not the trigger — `pokerarena-web`
// already has its own `@sentry/react`. Past that the entry hides
// `Failed to fetch (xyz.<domain>)`, the signal for a broken tagging host (TD-289).
//
// Exported ONLY so the loader-parity guard can assert this exact string against the copy in
// index.html. A behavioural matrix cannot police this list: it can only probe hosts it already
// names, so a host added here and forgotten there would pass green — the precise drift the
// paragraph above warns about. Not part of the lib's public filtering API; use the predicates.
export const THIRD_PARTY_TRACKER_HOSTS = String.raw`static\.ads-twitter\.com|(?:www\.)?googletagmanager\.com|xyz\.gtowizard\.com|xyz\.rbwizard\.com|xyz\.dailydoseofgto\.com|xyz\.pokerarena\.com`

const THIRD_PARTY_TRACKER_FRAME_RE = new RegExp(
  String.raw`^https?://(?:${THIRD_PARTY_TRACKER_HOSTS})/`,
  'i',
)

// FE-753 / FRONTEND-30RF: the same tracker beacon, but with the tracker host reaching us as the
// fetch TARGET in the message (`TypeError: Failed to fetch (xyz.gtowizard.com)`) while the only
// stack frame is the first-party `/hoph/…` wrapper. The frame gate above therefore never fires
// and the event leaked. Anchored whole-message on the tracker host in the target position, so
// the host must be what we failed to fetch — not merely a substring: a bare
// `/xyz\.gtowizard\.com/` would also match a first-party error that happens to quote the tracker
// URL (e.g. a CSP or ad-blocker report), which is exactly the over-match the frame gate's
// `^https?://host/` anchor exists to prevent.
//
// Case-INSENSITIVE, unlike VITE_PRELOAD_MESSAGE_RE / BARE_FETCH_FAILURE_RE, which forbid `/i`
// even though this gate — not those — is the 100% drop. The asymmetry is deliberate, and it is
// about what a case-variant can BE, not about how harshly it is treated. There, `/i` widens a
// message-only match onto `load failed`: no engine emits that, so it could only be a first-party
// error saying something else. Here, every match names a tracker in the TARGET position, so a
// case-variant is still a failed fetch OF a tracker whatever its casing — the set `/i` adds
// stays free of first-party errors, which is the only property the drop needs. It also matches
// the frame gate, keeping the two halves of one host list consistent. Near-dead either way:
// WebKit emits fixed-case literals and hosts arrive lowercase-normalized.
const THIRD_PARTY_TRACKER_TARGET_RE = new RegExp(
  String.raw`^(?:TypeError: )?(?:Load failed|Failed to fetch) \((?:${THIRD_PARTY_TRACKER_HOSTS})\)$`,
  'i',
)

export const isThirdPartyTrackerFetchRejection = (
  message: string,
  mechanismType: string,
  frames: readonly StackFrameLike[],
): boolean => {
  const msg = message.trim()

  if (!mechanismType.endsWith(UNHANDLED_REJECTION_MECHANISM_SUFFIX)) {
    return false
  }

  // (a) The tracker is on the stack — its beacon, its frame (308C / 30E0). BARE form only: an
  // annotated message names its own target, and gate (b) is the authority on that. See
  // BARE_FETCH_FAILURE_RE.
  if (
    BARE_FETCH_FAILURE_RE.test(msg) &&
    frames.some((frame) => THIRD_PARTY_TRACKER_FRAME_RE.test(frame.filename ?? ''))
  ) {
    return true
  }

  // (b) The tracker is the fetch TARGET, whatever the frame says (30RF). Deliberately NOT
  // gated on VITE_PRELOAD_MESSAGE_RE: that regex admits only a `gtowizard.com` suffix, which
  // would leave the ads-twitter / googletagmanager targets unreachable here. TARGET_RE is
  // already whole-message anchored over the same two message forms, so it needs no pre-gate.
  return THIRD_PARTY_TRACKER_TARGET_RE.test(msg)
}

// FE-948 / FRONTEND-30W4: the GTM container throwing inside its OWN code. Container
// `GTM-K3HXHPP` shipped a `zones` regression — `Un("zones")` returns a truthy object with no
// `isActive`, so `c.isActive(yl(), b)` throws at the container's event gate on every container
// execution. The container-side fix is MKT-1012; until it lands this is ~430 ev/day.
//
// FE-744 §3 deliberately left this class UNFILTERED, and that call was right at the time: the
// container runs synchronously on whatever stack pushed to `dataLayer`, so the throw was landing
// in our render path and taking out `/solutions` (FRONTEND-3112). FE-921 fenced all four
// first-party doorways (`withContainerFenced`), and the fence holds: every `vue.error_handler`
// event in the 14d after it shipped sits on a PRE-fence bundle, and the live window carries only
// the two mechanisms below. What is left is the container calling its own `dataLayer.push` from
// its own injected tag code or its own timer — nothing of ours on the stack, nothing of ours
// broken.
//
// `denyUrls` cannot reach this class: the container is served SAME-ORIGIN from a rotating cloaked
// path (`app.gtowizard.com/hoph/<token>`), so the innermost frame is first-party by URL. Hence a
// predicate rather than a URL deny entry.
//
// THREE independent conditions, because the message alone is not safe. `isActive` appears ~1,370
// times in first-party source, so a message-only gate (an `ignoreErrors` entry, say) would also
// swallow a genuine first-party `X.isActive is not a function`.
//
//  1. MESSAGE — start-anchored, and deliberately OPEN at the end: JavaScriptCore appends
//     `. (In 'c.isActive(tl(),b)', 'c.isActive' is undefined)`, whose inner callee is a minified
//     GTM symbol that rotates on every container rebuild (`tl`/`ql`/`yl`/`Al` all observed). A
//     `$` anchor would drop the Safari variants. The receiver is `c` in 100% of 7,049 live
//     events, but it is minified too, so the identifier class costs nothing and survives the next
//     minification.
//  2. THROW SITE — the LAST frame must belong to a tag container. Sentry orders frames
//     oldest-first, so the last one is where the throw happened: this asserts that CONTAINER code
//     threw, not merely that a container is somewhere on the stack. Deliberately not
//     `frames.every`: on the `browserapierrors` mechanisms the SDK's own wrapper sits at frame 0,
//     and `beforeSend` runs PRE-symbolication, where that wrapper is `/assets/<hash>.js` —
//     byte-identical to an app chunk. `every` would therefore match nothing on 74% of the volume.
//     If frame order ever changed, the last frame stops being a container frame and the event is
//     KEPT — the safe direction.
//  3. MECHANISM — an allow-list of the two container-INTERNAL paths. This is the condition that
//     keeps a future fence gap visible, and it is why (2) is not sufficient on its own: the
//     pre-fence FRONTEND-3112 events ALSO ended on a container frame, and they were a real
//     outage. A throw that reaches us through our own call site arrives as
//     `auto.function.vue.error_handler` (Vue's handler), a React boundary, or
//     `onunhandledrejection` (the router-hook path) — none of which are listed here, so all three
//     still report in full.
//
// SAMPLED + tagged, never a 100% drop (unlike the tracker beacons above, which can never become
// our bug). Two reasons. The container is still broken, so the tags it aborts are still LOST
// measurement — the canary is how we see MKT-1012 actually land. And a NEW unfenced doorway
// called from a timer or a bare event listener would arrive on these same mechanisms; at 1-in-50
// it stays visible as a trend instead of vanishing.
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA loader —
// the container script is injected by index.html itself, BEFORE the SPA bundle, so this class is
// reachable there.
const TAG_CONTAINER_MESSAGE_RE = /^(?:TypeError: )?[A-Za-z_$][\w$]*\.isActive is not a function\b/

// A frame belonging to the tag container. Two shapes, because the container reaches us from two
// origins: the enumerated tracker hosts (`xyz.gtowizard.com/jwodlhco.js`, `googletagmanager.com`,
// the Twitter pixel that loads the same broken code), and the CNAME-cloaked same-origin
// `/hoph/<token>` path. Only the literal `/hoph/` prefix is stable: the token rotates, and more
// than one token can appear in a single stack. So the second branch matches a PATH and makes the
// origin optional. At `beforeSend` time `filename` is a full URL — the tracker frame gate above
// relies on that, and it is confirmed working in production. The processed event shows the
// path-only form instead. Accepting both forms costs nothing.
// The optional origin is still pinned to a `gtowizard.com` authority immediately followed by
// `/hoph/`, so neither `evil.io/hoph/x` nor `app.gtowizard.com.attacker.io/hoph/x` can match, and
// `app.gtowizard.com/assets/x.js` — the app's own bundle — matches neither branch.
const TAG_CONTAINER_FRAME_RE = new RegExp(
  String.raw`^(?:https?://(?:${THIRD_PARTY_TRACKER_HOSTS})/|(?:https?://[a-z0-9.-]+\.gtowizard\.com)?/hoph/)`,
  'i',
)

// The container-INTERNAL mechanisms: the global `onerror` handler, and the SDK's browser-API
// instrumentation (`setTimeout`, `addEventListener`, …) wrapping a callback the CONTAINER
// registered. Enumerated rather than pattern-matched on purpose — an unlisted mechanism keeps its
// events, which is the guardrail-safe direction and the same under-match discipline as the
// tracker host list. The bare legacy `onerror` is covered for older SDK payloads. Every mechanism
// that carries a first-party call site is absent by construction — `auto.function.vue.error_handler`
// above all — and so is `auto.core.capture_console`, the mechanism the fence's own reports arrive
// on, so a fence report can never be swallowed by this gate.
const TAG_CONTAINER_INTERNAL_MECHANISM_RE =
  /^(?:auto\.browser\.(?:global_handlers\.onerror|browserapierrors\.\w+)|onerror)$/

export const isTagContainerInternalError = (
  message: string,
  exceptionType: string,
  mechanismType: string,
  frames: readonly StackFrameLike[],
): boolean => {
  if (exceptionType !== 'TypeError') {
    return false
  }

  if (!TAG_CONTAINER_MESSAGE_RE.test(message.trim())) {
    return false
  }

  if (!TAG_CONTAINER_INTERNAL_MECHANISM_RE.test(mechanismType)) {
    return false
  }

  // A frameless event cannot prove the container threw, so it is kept: `at(-1)` returns undefined
  // on an empty stack and the guard below rejects it. (`[length - 1]` would behave identically —
  // `at(-1)` is chosen only because it states "the last frame" directly.)
  const throwSite = frames.at(-1)

  return throwSite !== undefined && TAG_CONTAINER_FRAME_RE.test(throwSite.filename ?? '')
}

// FE-753 / FRONTEND-30VD: an UNHANDLED `TimeoutError: signal timed out` — an
// `AbortSignal.timeout()` rejection nobody awaited. Both first-party call sites are accounted
// for (the proxy probe in libs/analytics try/catches its own await; html-to-image@1.11.13
// try/catches every internal per-resource fetch in lib/dataurl.js), so the leak is most
// plausibly a third-party script or extension — unconfirmed, because Sentry raw-event access
// was unavailable at implementation time (recorded in FE-753). Per the ticket this is NOT a
// global `ignoreErrors: /signal timed out/`, which would also swallow a genuine handled
// timeout on login/solver/settings. The gate is shape-scoped on locale/minification-safe
// fields instead: the TimeoutError CLASS (exception `type` — a DOMException class name is
// never localized), the unhandled-rejection MECHANISM (a handled `captureException` arrives
// as `generic` and never matches), and framelessness (a thrown-but-unawaited browser timeout
// pins no throw site; an app-framed timeout stays actionable and is kept). Sampled + tagged,
// not dropped: a future first-party unawaited timeout would look identical, and the canary
// keeps it visible as a trend.
// NOT mirrored into apps/gto-wizard-web-vue/index.html's pre-SPA loader: nothing calls
// AbortSignal.timeout() before the SPA bundle loads, so the class cannot fire there. (Same
// reason isVueBroadcastChannelCloneError above is SPA-only.)
export const isUnhandledSignalTimeout = (
  exceptionType: string,
  mechanismType: string,
  frames: readonly unknown[],
): boolean =>
  exceptionType === 'TimeoutError' &&
  mechanismType.endsWith(UNHANDLED_REJECTION_MECHANISM_SUFFIX) &&
  frames.length === 0

// FE-753 / FRONTEND-30RH: WebKit surfaces a browser-cancelled fetch (navigation, page
// teardown) as a `TypeError` whose MESSAGE is the localized "cancelled" string — `已取消`,
// `annulé`, `cancelado`, `отменено`, … — so any message-based filter is per-locale
// whack-a-mole. (The `AbortError` class needs no equivalent here: Sentry's inboundFilters
// matches `ignoreErrors` against `${type}: ${value}`, and DOMException class names are never
// localized, so the existing /^(?:Error: )?AbortError/ entry already covers every locale.)
// This predicate keys ONLY on locale-independent, minification-surviving fields: the
// TypeError class, the unhandled-rejection mechanism, and framelessness. The EN fetch-failure
// forms are explicitly EXCLUDED — both the bare `Failed to fetch` / `Load failed` and, since
// FE-753 widened VITE_PRELOAD_MESSAGE_RE, the `(<host>.gtowizard.com)`-annotated form: those
// classes are owned by the tracker / vite-preload gates above, and taking their survivors here
// would double-sample their 1-in-50 canaries into silence. So a frameless annotated message
// lands in the vite-preload sampler, not here — the exclusion moves with that regex by
// construction, which is why it reads from the constant rather than restating the shape.
//
// The frame gate ships as strict framelessness — the fail-safe default. It was NOT verified
// against a raw 30RH event (Sentry access unavailable at implementation time): if live 30RH
// events carry frames (the FRONTEND-30E0 sibling was live-verified WITH a positioned minified
// frame), this filter is a no-op and the class keeps reporting — never an over-suppressor.
// Post-deploy validation in FE-753 checks which way it landed.
//
// A genuine network regression (offline, DNS, CDN outage) can produce this same shape, so the
// class is SAMPLED + tagged, never 100%-dropped. And because it is spread thinly across
// sessions (~1 event/session at 23 ev/day), the per-load counter alone would be no throttle
// at all — the FRONTEND-2PNZ lesson above — so the per-SESSION canary gate carries the actual
// cut (~2% of sessions report; the counter still caps a flooding session at 1-in-50).
// KEEP IN SYNC with the inlined copy in apps/gto-wizard-web-vue/index.html's pre-SPA loader.
export const isLocaleBlindFetchAbort = (
  message: string,
  exceptionType: string,
  mechanismType: string,
  frames: readonly unknown[],
): boolean =>
  exceptionType === 'TypeError' &&
  mechanismType.endsWith(UNHANDLED_REJECTION_MECHANISM_SUFFIX) &&
  frames.length === 0 &&
  !VITE_PRELOAD_MESSAGE_RE.test(message.trim())

export const initSentry = (app: App, router: Router) => {
  if (IS_PROD_ENV()) {
    let seenAdyenNetworkError = false
    let vueVdomDesyncCount = 0
    let nonErrorNoiseCount = 0
    let vueBroadcastChannelCloneCount = 0
    let vitePreloadAbortCount = 0
    let unhandledSignalTimeoutCount = 0
    let localeBlindFetchAbortCount = 0
    let tagContainerInternalCount = 0
    // FE-744: drawn ONCE per page load — the per-session half of the non-Error sampler. See
    // NOISE_CANARY_SESSION_RATE: the counter alone only throttles within a session, so a
    // class spread across many sessions needs a session-level gate to actually drop in volume.
    const isNonErrorCanarySession = Math.random() < NOISE_CANARY_SESSION_RATE
    // FE-753: per-session halves of the signal-timeout + fetch-abort samplers — same rationale
    // (both classes spread ~1 event per session, so only a session gate cuts real volume);
    // drawn independently so each class keeps its own ~2% canary population.
    const isSignalTimeoutCanarySession = Math.random() < NOISE_CANARY_SESSION_RATE
    const isFetchAbortCanarySession = Math.random() < NOISE_CANARY_SESSION_RATE
    // FE-948: same rationale again — the broken container throws many times per session for a
    // minority of sessions, so the counter alone would still ingest one event per affected
    // session. Drawn independently so this class keeps its own ~2% canary population.
    const isTagContainerCanarySession = Math.random() < NOISE_CANARY_SESSION_RATE
    Sentry.init({
      app,
      release: getCommitSha(),
      maxValueLength: 5000,
      dsn: 'https://30a4a3ad2d004feea44d5adedea8a9e4@o446641.ingest.sentry.io/4504118442983424', // gwizteam
      integrations: [
        Sentry.browserTracingIntegration({
          router,
        }),
        Sentry.captureConsoleIntegration({ levels: ['debug'] }),
      ],
      tracesSampleRate: 0,
      ignoreErrors: [
        // TRAINER SOUND on some devices that have it disabled
        'NotSupportedError: Failed to load because no supported source was found.',
        // TRAINER SOUND error if user didnt interact with page first
        'NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.',

        // Lazy load bundles handled by vite:preloadError
        /^Importing a module script failed/,
        /^Failed to fetch dynamically imported module/,
        /^Error loading dynamically imported module/,

        'Rendered items limit reached', // infinite scroller when someone turns off css height it fires thousands of events
        'Network Error',
        'Cannot redefine property: googletag',
        'Cannot set property chainId of #<D> which has only a getter',
        'Cannot set property chainId of #<J> which has only a getter', // some eth wallet extension
        'something went wrong TypeError: a.ethereum.initializeProvider is not a function',
        'something went wrong  TypeError: a.ethereum.initializeProvider is not a function',
        'Non-Error promise rejection captured with keys: currentTarget, isTrusted, target, type', // something with PWA reload on /coaching
        `Cannot read properties of undefined (reading 'outerHTML')`, // some error from unknown browser
        'ResizeObserver loop limit exceeded', // ResizeObserver
        'ResizeObserver loop completed with undelivered notifications.', // ResizeObserver
        'Object.keys called on non-object', // FB
        'Detected container element removed from DOM', // PayPal BTN
        'Can not send postrobot_method. Target window is closed', // PayPal BTN
        'Avoided redundant navigation to current location', // Vue Router
        `Can't find variable: instantSearchSDKJSBridgeClearHighlight`, // Bing
        `Can't find variable: _AutofillCallbackHandler`, // Instagram in-app browser autofill injection (FRONTEND-2QQ6)
        /__firefox__/, // Firefox iOS WKWebView injected helpers, e.g. refresh_youtube_quality_, reader; also the no-prefix WebKit `Can't find variable: __firefox__` form (FRONTEND-KK1, 2X52)
        /window\.ethereum\.selectedAddress/, // injected wallet provider in mobile in-app browsers (FRONTEND-KK1)
        `undefined is not an object (evaluating 'document.getElementsByTagName('video')[0].webkitExitFullScreen')`, // YouTube
        'e.$refs.content.contains', // Vue youtube
        'this.player.setSize is not a function', // Vue youtube
        `Cannot read properties of null (reading 'CodeMirror')`, // CodeMirror
        `Cannot read properties of undefined (reading 'firefoxSample')`, // Chrome extension 'OrangeMonkey'.
        'XDEFI: Failed to start XDEFIWeb3Provider: TypeError: Cannot redefine property: solana',
        'Disconnected from OKX Wallet background. Page reload required',
        // ChromeOS specific errors.
        'Request timeout isMathOcrAvailable',
        'Request timeout getDictionariesByLanguageId',
        'Request timeout getSupportScreenShot',
        'Request timeout isDictateAvailable',
        'Request timeout isPredictionAvailable',
        'Request timeout dictionariesDistributor.getValue',
        'Request timeout speechVoicesDistributor.getValue',
        'Request timeout userDistributor.getValue',
        'Request timeout predictionDistributor.getValue',
        'Request timeout dictateStateDistributor.getValue',
        'Request timeout nn-NO_wordsDistributor.getValue',
        'Request timeout availableTextCheckLanguagesDistributor.getValue',
        'Request timeout es_wordsDistributor.getValue',
        'Request timeout lettersVoicesDistributor.getValue',
        'Request timeout topicsDistributor.getValue',
        'Request timeout availableLanguagesDistributor.getValue',
        'Request timeout ac_wordsDistributor.getValue',
        'Request timeout nb-NO_wordsDistributor.getValue',
        'Request timeout ua_wordsDistributor.getValue',
        'Request timeout en_wordsDistributor.getValue',
        'Request timeout appSettingsDistributor.getValue',
        'Request timeout DefineExpirationForLanguagePacks.getValue',
        'Request timeout textCheckersDistributor.getValue',
        'Request timeout de_wordsDistributor.getValue',
        'Request timeout fr_wordsDistributor.getValue',
        'Request timeout ru_wordsDistributor.getValue',
        'Request timeout ToolbarStatus',
        'Request timeout getSelectedText',
        'Object Not Found Matching Id',

        // --- RECOMENDED LIST
        // https://gist.github.com/Chocksy/e9b2cdd4afc2aadc7989762c4b8b495a
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        `Can't find variable: ZiteReader`,
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Generic error code from errors outside the security sandbox
        // Avast extension error
        '_avast_submit',

        // Some random error from some extension most likely triggering 1000x times
        // https://gtowizard.sentry.io/issues/5155228321/?project=4504118442983424&query=is%3Aunresolved&referrer=issue-stream&sort=freq&statsPeriod=14d&stream_index=0
        'null has no properties',

        // Vue transition error
        // https://gtowizard.sentry.io/issues/5110014700/?project=4504118442983424&query=is%3Aunresolved&referrer=issue-stream&sort=freq&statsPeriod=14d&stream_index=3
        'S.el.getBoundingClientRect is not a function',

        // Auth token refresh noise
        'Token refresh failed',

        // FE-482: user-cancelled requests and connection lifecycle noise
        // `(?:Error: )?` also catches the wrapped `Error: AbortError: …` form (FRONTEND-Y3)
        /^(?:Error: )?AbortError/,
        /Request aborted/,
        /Connection is closing/,
        /Unable to preload CSS/,

        // FE-714: new third-party noise classes. These match the event message, not frame
        // URLs — used where the noisy frame is an obfuscated third-party wrapper that
        // denyUrls' innermost-frame test would miss, or where the vendor's domain is in
        // the message itself.
        /googlesyndication\.com/, // AdSense `Failed to fetch (…googlesyndication.com)` — pagead wrapper frame evades denyUrls (FRONTEND-2ZW8)
        'There was an error fetching the embed code from Vimeo', // Vimeo geo-block embed failure (FRONTEND-2HPQ)
      ],
      denyUrls: [
        // Google Adsense
        /pagead\/js/i,
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        // FE-714: third-party scripts whose every frame is the vendor bundle, so the
        // innermost-frame denyUrls test fires precisely (guardrail-safe under-matching).
        /beacon\.min\.js/, // Cloudflare Web Analytics RUM — `.at is not a function` on old Chromium (FRONTEND-30T0, 30RN)
        /clarity\.(?:js|ms)\b/i, // MS Clarity SDK errors — orthogonal to attachClarityTags enrichment (FRONTEND-2ZKJ)
      ],
      // INT-15: strip a partner import hand from the navigation breadcrumb the
      // fragment-stripping `replaceState` itself records. See scrubHandFragment.
      beforeBreadcrumb: (breadcrumb) => scrubHandFragmentFromBreadcrumb(breadcrumb),
      beforeSend: (event) => {
        // FIRST: every `return null` below drops the event, but the ones that return it
        // must not carry the hand in `event.request.url`. See scrubHandFragment.
        scrubHandFragmentFromEvent(event)
        attachClarityTags(event)

        // Don't pollute Sentry with debug messages not related to our app
        if (event.level === 'debug' && !isSentryLogMessage(event.message)) {
          return null
        }

        const { msg, exceptionType, frames, mechanismType } = getExceptionDetails(event)

        if (isInjectedScriptStackOverflow(msg, frames)) {
          return null
        }

        // FE-714 / FRONTEND-DSX: the resource-load `Event` (type=error) subclass is the #1
        // raw-event flood and unactionable (frameless DOM Event, no stack). Drop it 100%
        // here — a STRICT SUBSET of the non-Error class — *before* the sampler below, so the
        // wallet / Script-error siblings keep their 1-in-50 SDK-drift canary (R-d). Must sit
        // before isUnsymbolicatableNonError, which also matches this shape.
        if (isResourceLoadErrorEvent(msg, frames)) {
          return null
        }

        // FE-485: non-Error / third-party noise that has no app stack and can never
        // symbolicate (wallet-extension objects, cross-origin "Script error.") — captured by
        // the browser global handlers, not our code. Sampled (not a silent 100% drop) and
        // tagged, so the suppressed class stays observable if the SDK message format shifts
        // again. (The DSX resource-load Event subclass is dropped 100% above.)
        // FE-744: sampling is now BOTH a per-session canary gate and the 1-in-50 counter — the
        // counter alone let the first event of every session through, which is no throttle at
        // all for a class spread across 542 users. See NOISE_CANARY_SESSION_RATE.
        if (isUnsymbolicatableNonError(msg, frames)) {
          nonErrorNoiseCount += 1
          if (!shouldIngestSampledNoise(isNonErrorCanarySession, nonErrorNoiseCount)) {
            return null
          }
          event.tags = { ...event.tags, sampled_noise: 'non_error_global_capture' }
        }

        // FRONTEND-2WST / FRONTEND-2H6A: sample the Vue vdom-desync flood. The Practice
        // route boundary already recovers + dedupes the trainer case; this keeps whatever
        // still reaches Sentry from other routes visible without the self-amplifying flood.
        // Tag the survivors (FE-714): the broadened parentNode/nextSibling variants can match
        // a genuine first-party crash the weak mechanism gate can't exclude, so tagging keeps
        // one greppable among the flood (never a 100% drop — see isVueVdomDesyncError).
        if (isVueVdomDesyncError(msg, mechanismType)) {
          vueVdomDesyncCount += 1
          if (vueVdomDesyncCount % NOISE_SAMPLE_EVERY !== 1) {
            return null
          }
          event.tags = { ...event.tags, sampled_noise: 'vue_vdom_desync' }
        }

        // FE-744 / FRONTEND-2ZM8: the Vue trainer's non-cloneable BroadcastChannel payload.
        // Sampled + tagged, NOT dropped — it is our bug, so the trend has to stay visible (see
        // isVueBroadcastChannelCloneError). Mechanism-gated, so the React equivalent (which
        // would be a genuine, actionable bug) is never swept in.
        if (isVueBroadcastChannelCloneError(msg, exceptionType, mechanismType)) {
          vueBroadcastChannelCloneCount += 1
          if (vueBroadcastChannelCloneCount % NOISE_SAMPLE_EVERY !== 1) {
            return null
          }
          event.tags = { ...event.tags, sampled_noise: 'vue_broadcast_channel_clone' }
        }

        // silence freshchat 3rd party script errors
        // https://gtowizard.sentry.io/issues/6547693984/events/cfb6f09124e040f8b371b201eb2f6afc/?project=4504118442983424
        // https://gtowizard.sentry.io/issues/6590990915/?project=4504118442983424
        const fromFreshchat = frames.some((f) => (f.filename ?? '').includes('/js/widget.js'))
        if (fromFreshchat) {
          return null
        }

        // silence service worker rejections from bots
        // https://gtowizard.sentry.io/issues/6534388830/
        const touchesRegisterSW = frames.some((f) =>
          (f.function ?? '').includes('serviceWorker.register'),
        )
        const ua =
          event.request?.headers?.['User-Agent'] ??
          event.request?.headers?.['user-agent'] ??
          (typeof navigator === 'undefined' ? '' : navigator.userAgent)
        const isBot = /(bot|crawler|spider|crawling|headless|lighthouse|Google-Read-Aloud)/i.test(
          ua,
        )
        if (touchesRegisterSW && /Rejected/i.test(msg) && isBot) {
          return null // drop noisy bot rejections
        }

        // FE-744 / FRONTEND-308C + 30E0 + 30RF: a third-party tracker's own failed fetch beacon.
        // MUST stay ahead of the Vite-preload sampler below: both match the same
        // `Failed to fetch` + unhandled-rejection shape, and this one is the more specific
        // (tracker frame- OR target-anchored) class and a 100% drop, so it has to win. Same
        // ordering discipline as isResourceLoadErrorEvent ahead of the non-Error sampler.
        if (isThirdPartyTrackerFetchRejection(msg, mechanismType, frames)) {
          return null
        }

        // FE-948 / FRONTEND-30W4: the broken GTM container throwing inside its own code — see
        // isTagContainerInternalError. Order-independent of every gate around it (no other class
        // matches a `TypeError: …isActive is not a function`), so it sits next to the tracker gate
        // for topical grouping rather than for precedence. Session-gated 1-in-50 sample + tag, not
        // a drop: the canary is how MKT-1012's container fix gets verified, and how a new unfenced
        // doorway on these same mechanisms would still surface.
        if (isTagContainerInternalError(msg, exceptionType, mechanismType, frames)) {
          tagContainerInternalCount += 1
          if (!shouldIngestSampledNoise(isTagContainerCanarySession, tagContainerInternalCount)) {
            return null
          }
          event.tags = { ...event.tags, sampled_noise: 'tag_container_internal' }
        }

        // FE-725 / FRONTEND-30E0: iOS Safari fetch-abort in Vite's modulepreload polyfill —
        // suppress regardless of tab visibility (it fires while still `visible`, so it leaks
        // past the FE-482 hidden-tab guard below). Mechanism-scoped to unhandled rejections
        // (the frame is minified pre-symbolication — see isVitePreloadFetchAbort), so a handled
        // first-party `Load failed` from unrelated app code is never swept in. Sampled 1-in-50
        // + tagged (not a 100% drop) because WebKit reuses the bare `Load failed` message for
        // every fetch failure — the canary keeps a real preload-network regression visible.
        if (isVitePreloadFetchAbort(msg, mechanismType)) {
          vitePreloadAbortCount += 1
          if (vitePreloadAbortCount % NOISE_SAMPLE_EVERY !== 1) {
            return null
          }
          event.tags = { ...event.tags, sampled_noise: 'vite_preload_fetch_abort' }
        }

        // FE-753 / FRONTEND-30VD: unhandled AbortSignal.timeout() rejection — see
        // isUnhandledSignalTimeout. Shape-scoped (TimeoutError class + unhandled-rejection
        // mechanism + frameless), so a handled timeout (mechanism `generic`) or an app-framed
        // one never matches. Session-gated 1-in-50 sample + tag, same machinery as the
        // fetch-abort class below.
        if (isUnhandledSignalTimeout(exceptionType, mechanismType, frames)) {
          unhandledSignalTimeoutCount += 1
          if (
            !shouldIngestSampledNoise(isSignalTimeoutCanarySession, unhandledSignalTimeoutCount)
          ) {
            return null
          }
          event.tags = { ...event.tags, sampled_noise: 'signal_timeout' }
        }

        // FE-753 / FRONTEND-30RH: locale-blind fetch-abort/cancellation — see
        // isLocaleBlindFetchAbort. MUST stay after the tracker + vite-preload gates: the bare
        // EN fetch-failure forms are excluded inside the predicate precisely so those classes
        // keep their own canaries; every other frameless unhandled TypeError (the localized
        // cancellation family) is owned here.
        if (isLocaleBlindFetchAbort(msg, exceptionType, mechanismType, frames)) {
          localeBlindFetchAbortCount += 1
          if (!shouldIngestSampledNoise(isFetchAbortCanarySession, localeBlindFetchAbortCount)) {
            return null
          }
          event.tags = { ...event.tags, sampled_noise: 'fetch_abort' }
        }

        // FE-482: drop Failed to fetch / Load failed when the page is hidden —
        // these are browser-cancelled requests triggered by navigation or tab switches
        if (
          /Failed to fetch|Load failed/i.test(msg) &&
          typeof document !== 'undefined' &&
          document.visibilityState !== 'visible'
        ) {
          return null
        }

        // FE-482: Adyen NETWORK_ERROR floods — allow the first to surface for alerting,
        // then silence the rest for the session
        const isAdyenNetworkError =
          msg.includes('checkoutshopper-live.adyen.com') && /Failed to fetch/i.test(msg)
        if (isAdyenNetworkError) {
          if (seenAdyenNetworkError) {
            return null
          }
          seenAdyenNetworkError = true
        }

        return event
      },
    })
  }
}

export const setSentryUser = (user: { email: string }) => {
  Sentry.setUser(user)
}

function getClarityId(name: string) {
  const match = new RegExp(`${name}=([^;]+)`).exec(document.cookie)
  if (!match || match[1] === undefined || match[1] === '') {
    return undefined
  }

  return decodeURIComponent(match[1]).split('^')[0]
}

function attachClarityTags(event: Sentry.Event) {
  const clarityProjectId = import.meta.env.VITE_MS_CLARITY_ID as string | undefined

  if (clarityProjectId === undefined || clarityProjectId === '') {
    return
  }

  const userId = getClarityId('_clck')

  if (userId === undefined || userId === '') {
    return
  }

  const sessionId = getClarityId('_clsk')
  event.tags = {
    ...event.tags,
    clarity_user_id: userId,
    ...(sessionId !== undefined && sessionId !== ''
      ? {
          clarity_session_url: `https://clarity.microsoft.com/player/${clarityProjectId}/${userId}/${sessionId}`,
        }
      : {}),
  }
}
